// src/views/adminView/contractsView/detalle/DetalleIntegracionesTab.jsx
// Tab "Integraciones" del detalle de empresa: solo las integraciones activas
// del contract (contract.integrations), con el logo real de cada plataforma.
// La conexión de integraciones es responsabilidad de cada cliente desde su
// propio portal, así que acá no hay catálogo ni acciones de conexión.
import { useTranslation } from "react-i18next";
import { Plug } from "lucide-react";
import { Card, CardHeader, CardTitle } from "../../../../components/ui/Card";
import { Badge } from "../../../../components/ui/Badge";
import { EmptyState } from "../../../../components/ui/EmptyState";
import { iconList } from "../../../../utils/iconList";
import { posibleIntegrations } from "../../../../utils/integrations";
import { cn } from "../../../../utils/cn";

// integrationName del backend → entrada del catálogo (icono). Case-insensitive
// porque el catálogo usa "TiendaNube"/"MercadoLibre" y el dato guardado debe
// matchear aunque difiera el casing.
const findCatalogEntry = (integrationName) =>
  posibleIntegrations.find(
    (p) => p.name.toLowerCase() === String(integrationName || "").toLowerCase()
  );

// Cuenta asociada, con la misma prioridad que la vista de integraciones del
// cliente: sender_id → dominio de la tienda → url → alias.
const accountLabel = (integ) =>
  integ?.sender_id ||
  (integ?.shopDomain && integ.shopDomain.replace(/^https?:\/\//, "")) ||
  (integ?.url && integ.url.replace(/^https?:\/\//, "")) ||
  integ?.alias ||
  null;

function IntegrationLogo({ entry }) {
  const src = entry ? iconList[entry.icon] : null;
  return (
    <div
      className={cn(
        "h-10 w-10 shrink-0 rounded-lg border border-border bg-bg-muted flex items-center justify-center p-1.5",
        // Chips de contraste heredados del catálogo del cliente: Ripley y
        // Jumpseller traen wordmark blanco (necesitan fondo oscuro en light).
        (entry?.name === "Ripley" || entry?.name === "Jumpseller") &&
          "bg-fg border-fg dark:bg-bg-muted dark:border-border"
      )}
      aria-hidden="true"
    >
      {src ? (
        <img src={src} alt="" className="h-full w-full object-contain dark-safe-img" />
      ) : (
        <Plug className="w-4 h-4 text-fg-muted" />
      )}
    </div>
  );
}

export default function DetalleIntegracionesTab({ contract }) {
  const { t } = useTranslation();
  const integrations = Array.isArray(contract?.integrations) ? contract.integrations : [];

  return (
    <Card>
      <CardHeader>
        <CardTitle>{t("Integraciones activas")}</CardTitle>
      </CardHeader>
      {integrations.length === 0 ? (
        <EmptyState
          icon={<Plug aria-hidden="true" />}
          title={t("Sin integraciones conectadas")}
        />
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          {integrations.map((integ, i) => {
            const entry = findCatalogEntry(integ?.integrationName);
            const account = accountLabel(integ);
            return (
              <div
                key={i}
                className="flex items-center gap-3 rounded-lg border border-border bg-bg p-3"
              >
                <IntegrationLogo entry={entry} />
                <div className="min-w-0 flex-1">
                  <div className="text-sm font-semibold text-fg truncate">
                    {entry?.name || integ?.integrationName || "—"}
                  </div>
                  {account && (
                    <div className="text-xs text-fg-muted truncate">{account}</div>
                  )}
                </div>
                <Badge tone="success" dot>
                  {t("Conectada")}
                </Badge>
              </div>
            );
          })}
        </div>
      )}
    </Card>
  );
}
